'use client';

import { subtitle as financialAdviceSubtitle } from '../data/financialAdviceConstants';
import { subtitle as generositySubtitle } from '../data/generosityConstants';
import { subtitle as productsSubtitle } from '../data/productConstants';
import { subtitle as resourcesSubtitle } from '../data/resourceConstants';
import { subtitle as whyThriventSubtitle } from '../data/whyThriventConstants';
import {
  AdvisorButton,
  BackButtonContainer,
  Backdrop,
  ButtonsContainer,
  LoginButton,
  Menu,
  MenuBodyMain,
  MenuBodySecondary,
  MenuFooter,
  MenuHeader,
  NavIconClose,
  NavIconMenu,
  NavListItem,
  NavSubTitle,
  NavTitle,
  NavTitleContainer,
  TopLeftClickable,
} from './HeaderMobileMenu.style';
import { FinancialAdviceMobileMenu } from './financialAdvice/FinancialAdviceMobileMenu';
import { ProductMobileMenu } from './products/ProductMobileMenu';
import { ResourcesMobileMenu } from './resources/ResourcesMobileMenu';
import { WhyThriventMobileMenu } from './whyThrivent/WhyThriventMobileMenu';
import { useBreakpoint } from '@exploration/ui-utils';
import { Menu_Hamburger as MenuHamburger } from '@thrivent/midwest-web';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

const FINANCIAL_ADVICE = 'Financial-Advice';
const PRODUCTS = 'Products';
const WHY_THRIVENT = 'Why-Thrivent';
const GENEROSITY = 'Generosity';
const RESOURCES = 'Resources';

export const HeaderMobileMenu = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [activeNav, setActiveNav] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [requestClose, setRequestClose] = useState(false);
  const bp = useBreakpoint();
  const { midwestColors: colors } = useTheme();

  useEffect(() => {
    if (menuOpen) {
      if (ref.current) {
        ref.current.classList.remove('animate__slideOutRight');
        ref.current.classList.add('animate__slideInRight');
      }
      document.body.setAttribute('data-scroll-locked', 'true');
    } else {
      document.body.setAttribute('data-scroll-locked', 'false');
    }

    return () => {
      document.body.setAttribute('data-scroll-locked', 'false');
    };
  }, [menuOpen]);

  useEffect(() => {
    if (requestClose) {
      if (ref.current) {
        ref.current.classList.remove('animate__slideInRight');
        document.body.setAttribute('data-scroll-locked', 'false');

        ref.current.classList.add('animate__slideOutRight');
        document.body.setAttribute('data-scroll-locked', 'true');
        ref.current.addEventListener(
          'animationend',
          () => {
            setMenuOpen(false);
          },
          { once: true },
        );
      } else {
        setMenuOpen(false);
      }

      setRequestClose(false);
    }
  }, [requestClose]);

  useEffect(() => {
    if (bp !== 'mobile' && bp !== 'tablet') {
      setMenuOpen(false);
      document.body.setAttribute('data-scroll-locked', 'false');
    }
  }, [bp]);

  function open() {
    document.body.classList.add('header-is-open');
    setMenuOpen(!menuOpen);
  }

  function close() {
    setActiveNav('');
    setRequestClose(true);
  }

  const chevronSVG = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <g id="Chevron Right">
          <path
            id="icon"
            d="M6.15001 17.5083C6.55835 17.9167 7.21668 17.9167 7.62501 17.5083L14.55 10.5833C14.875 10.2583 14.875 9.73334 14.55 9.40834L7.62501 2.48334C7.21668 2.07501 6.55835 2.07501 6.15001 2.48334C5.74168 2.89168 5.74168 3.55001 6.15001 3.95834L12.1833 10L6.14168 16.0417C5.74168 16.4417 5.74168 17.1083 6.15001 17.5083Z"
            fill={colors.componentButtonColorBackgroundStrongPrimaryDefault}
          />
        </g>
      </svg>
    );
  };

  const closeSVG = () => {
    return (
      <svg
        width="24px"
        height="24px"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
      >
        <path fill="none" stroke="none" d="M0 0h24v24H0z"></path>
        <path
          stroke="none"
          d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
        ></path>
      </svg>
    );
  };

  const returnMenuBasedOnActiveNav = () => {
    switch (activeNav) {
      case FINANCIAL_ADVICE:
        return (
          <MenuBodySecondary>
            <FinancialAdviceMobileMenu />
          </MenuBodySecondary>
        );
      case PRODUCTS:
        return (
          <MenuBodySecondary>
            <ProductMobileMenu />
          </MenuBodySecondary>
        );
      case WHY_THRIVENT:
        return (
          <MenuBodySecondary>
            <WhyThriventMobileMenu />
          </MenuBodySecondary>
        );
      case GENEROSITY:
        return (
          <MenuBodySecondary>
            <div>Generosity</div>
          </MenuBodySecondary>
        );
      case RESOURCES:
        return (
          <MenuBodySecondary>
            <ResourcesMobileMenu />
          </MenuBodySecondary>
        );
      default:
        return (
          <MenuBodyMain className="active">
            <NavListItem
              onClick={() => {
                setActiveNav(FINANCIAL_ADVICE);
              }}
              data-analytics-id="financial-advice"
            >
              <NavTitleContainer>
                <NavTitle>Financial Advice</NavTitle>
                <div>{chevronSVG()}</div>
              </NavTitleContainer>
              <NavSubTitle>{financialAdviceSubtitle}</NavSubTitle>
            </NavListItem>
            <NavListItem
              onClick={() => {
                setActiveNav(PRODUCTS);
              }}
              data-analytics-id="products"
            >
              <NavTitleContainer>
                <NavTitle>Products</NavTitle>
                <div>{chevronSVG()}</div>
              </NavTitleContainer>
              <NavSubTitle>{productsSubtitle}</NavSubTitle>
            </NavListItem>
            <NavListItem
              onClick={() => {
                setActiveNav(WHY_THRIVENT);
              }}
              data-analytics-id="why-thrivent"
            >
              <NavTitleContainer>
                <NavTitle>Why Thrivent</NavTitle>
                <div>{chevronSVG()}</div>
              </NavTitleContainer>
              <NavSubTitle>{whyThriventSubtitle}</NavSubTitle>
            </NavListItem>
            <NavListItem
              onClick={() => {
                setActiveNav(GENEROSITY);
              }}
              data-analytics-id="generosity"
            >
              <NavTitleContainer>
                <NavTitle>Generosity</NavTitle>
                <div>{chevronSVG()}</div>
              </NavTitleContainer>
              <NavSubTitle>{generositySubtitle}</NavSubTitle>
            </NavListItem>
            <NavListItem
              onClick={() => {
                setActiveNav(RESOURCES);
              }}
              data-analytics-id="resources"
            >
              <NavTitleContainer>
                <NavTitle>Resources</NavTitle>
                <div>{chevronSVG()}</div>
              </NavTitleContainer>
              <NavSubTitle>{resourcesSubtitle}</NavSubTitle>
            </NavListItem>
          </MenuBodyMain>
        );
    }
  };

  return (
    <>
      <NavIconMenu
        onClick={open}
        data-analytics-id="mobile-nav-menu-open"
        aria-label="Menu Open"
      >
        <MenuHamburger
          fill={colors.componentButtonColorBackgroundStrongPrimaryDefault}
        />
        <div>Menu</div>
      </NavIconMenu>

      {menuOpen && (
        <>
          <Backdrop onClick={close} />
          <Menu
            ref={ref}
            className="animate__animated animate__faster"
            role="dialog"
            data-analytics-name="mobile-nav-menu"
          >
            <MenuHeader>
              <TopLeftClickable
                onClick={
                  activeNav === '' ? () => close() : () => setActiveNav('')
                }
                data-analytics-id="home"
              >
                <BackButtonContainer>
                  {activeNav === '' ? <>Menu</> : <>{chevronSVG()}Back</>}
                </BackButtonContainer>
              </TopLeftClickable>
              <NavIconClose
                onClick={close}
                data-analytics-id="mobile-nav-menu-close"
                aria-label="Menu Close"
              >
                {closeSVG()}
              </NavIconClose>
            </MenuHeader>
            {returnMenuBasedOnActiveNav()}
            <MenuFooter>
              <ButtonsContainer>
                <LoginButton />
              </ButtonsContainer>

              <AdvisorButton
                fullWidth
                variant="secondary"
                href="https://local.thrivent.com"
                data-analytics-id="find-financial-advisors"
              >
                Find financial advisors
              </AdvisorButton>
            </MenuFooter>
          </Menu>
        </>
      )}
    </>
  );
};
